import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import * as authService from "../services/autService";

const extractError = (error: unknown) => {
  if (!error) return "Erreur inconnue.";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  const maybeMessage = (error as { message?: string }).message;
  return maybeMessage ?? "Impossible de confirmer le compte.";
};

const ConfirmAccountPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState(
    "Confirmation de votre compte en cours..."
  );
  const [email, setEmail] = useState("");
  const [resendState, setResendState] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [resendMessage, setResendMessage] = useState("");
  const { toast } = useToast();

  const token = useMemo(() => searchParams.get("token"), [searchParams]);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage(
        "Lien invalide ou expiré. Demandez un nouvel e-mail de confirmation."
      );
      return;
    }
    const run = async () => {
      try {
        await authService.confirmAccount({ token });
        setStatus("success");
        setMessage(
          "Votre compte est confirmé. Vous pouvez maintenant vous connecter."
        );
      } catch (error) {
        setStatus("error");
        setMessage(extractError(error));
      }
    };
    void run();
  }, [token]);

  const handleResend = async (event: FormEvent) => {
    event.preventDefault();
    if (!email) {
      setResendState("error");
      setResendMessage("Veuillez saisir votre adresse e-mail.");
      return;
    }
    setResendState("sending");
    setResendMessage("");
    try {
      await authService.resendConfirmation({ email });
      setResendState("sent");
      setResendMessage(
        "Un nouveau lien d’activation vient de vous être envoyé."
      );
      toast({
        title: "E-mail envoyé",
        description:
          "Si l’adresse existe, vous recevrez un nouveau lien de confirmation d’ici peu.",
        variant: "success",
      });
    } catch (error) {
      setResendState("error");
      setResendMessage(extractError(error));
    }
  };

  return (
    <section className="mx-auto max-w-2xl rounded-3xl border border-border bg-card p-6 text-foreground shadow-lg md:p-10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-2xl">
      <div className="space-y-3 text-center">
        <h1 className="text-2xl font-semibold">
          {status === "success"
            ? "Compte confirmé"
            : status === "error"
            ? "Confirmation impossible"
            : "Confirmation en cours"}
        </h1>
        <p className="text-muted-foreground dark:text-white/70">{message}</p>
      </div>

      {status === "loading" && (
        <div className="mt-8 flex justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-foreground dark:border-white/20 dark:border-t-white" />
        </div>
      )}

      {status === "success" && (
        <div className="mt-8 flex flex-col items-center gap-4">
          <Button asChild>
            <Link to="/login">Se connecter</Link>
          </Button>
          <p className="text-sm text-muted-foreground dark:text-white/60">
            Une fois connecté, vous pourrez accéder à l’ensemble des services.
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-10 space-y-6">
          <form onSubmit={handleResend} className="space-y-4">
            <div className="space-y-2 text-left">
              <Label htmlFor="resendEmail">Adresse e-mail</Label>
              <Input
                id="resendEmail"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="votre.email@exemple.com"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={resendState === "sending"}
              className="w-full">
              {resendState === "sending"
                ? "Envoi en cours…"
                : "Renvoyer le lien"}
            </Button>
            {resendMessage && (
              <p
                className={`text-sm ${
                  resendState === "sent"
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-red-600 dark:text-red-300"
                }`}>
                {resendMessage}
              </p>
            )}
          </form>
          <div className="rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/70">
            <p className="font-semibold text-foreground dark:text-white">
              Besoin d’aide ?
            </p>
            <p>
              Vérifiez que l’adresse est correcte, regardez vos spams, ou
              contactez l’administrateur si le problème persiste.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default ConfirmAccountPage;
